/**
 * Helpers for identifying emergency contacts from FHIR RelatedPerson resources.
 *
 * FHIR RelatedPerson uses `relationship` codings. The emergency-contact relationship
 * code "C" (from v2-0131) indicates an emergency contact.
 */
import type { RelatedPerson } from '@medplum/fhirtypes';

const EMERGENCY_CONTACT_CODES = new Set(['C', 'E', 'emergency']);

/**
 * Returns true if the RelatedPerson is explicitly marked as an emergency contact
 * via its relationship coding. Checks the v2 table 0131 code "C" or custom
 * "emergency" code.
 */
export function isEmergencyContactRelatedPerson(rp: RelatedPerson): boolean {
  for (const cc of rp.relationship ?? []) {
    for (const coding of cc.coding ?? []) {
      if (coding.code && EMERGENCY_CONTACT_CODES.has(coding.code)) return true;
    }
    if (cc.text?.toLowerCase().includes('emergency')) return true;
  }
  return false;
}

export interface EmergencyContactDisplay {
  id: string;
  name: string;
  relationship: string;
  phone: string | undefined;
}

export function relatedPersonToEmergencyContactDisplay(
  rp: RelatedPerson,
): EmergencyContactDisplay {
  const n = rp.name?.[0];
  const name = n
    ? [n.prefix?.[0], ...(n.given ?? []), n.family].filter(Boolean).join(' ').trim()
    : 'Emergency Contact';

  const relationship =
    rp.relationship?.[0]?.coding?.[0]?.display ??
    rp.relationship?.[0]?.text ??
    'Emergency Contact';

  const phone = rp.telecom?.find((t) => t.system === 'phone')?.value;

  return {
    id: rp.id ?? `rp-${name}`,
    name,
    relationship,
    phone,
  };
}
